import React, { useState, useEffect } from 'react';
import { Loader2, Sparkles, BookOpen, Clock, Target, RefreshCw } from 'lucide-react';
import EnhancedMarkdown from '../CP-News/EnhancedMarkdown';
import useAcademySynthesis from './useAcademySynthesis';

/**
 * Composant de synthèse intelligente pour l'espace Academy
 * Affiche une synthèse de découverte des vidéos basée sur l'objectif de l'utilisateur
 * Approche universelle pour tous les types de contenu : professionnel, personnel, tutoriels, inspiration
 * Inspiré du design de NewsSynthesisCard pour harmoniser la présentation
 */
const AcademySynthesisCard = ({ 
  videos = [], 
  objective, 
  userId,
  onSynthesisGenerated 
}) => {
  const [synthesis, setSynthesis] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  
  // Utiliser le hook amélioré pour la synthèse
  const { generateSynthesis: generateSynthesisHook } = useAcademySynthesis(userId);

  // Générer la synthèse quand les vidéos ou l'objectif changent
  useEffect(() => {
    if (videos.length > 0 && objective) {
      generateSynthesis();
    }
  }, [videos, objective]);

  const generateSynthesis = async () => {
    if (!videos.length || !objective) return;

    setIsGenerating(true);
    setError(null);

    try {
      // Utiliser le hook amélioré avec l'approche universelle
      const synthesisData = await generateSynthesisHook(videos, objective);
      
      setSynthesis(synthesisData);
      onSynthesisGenerated?.(synthesisData);

    } catch (err) {
      console.error('Erreur génération synthèse:', err);
      setError('Erreur lors de la génération de la synthèse');
    } finally {
      setIsGenerating(false);
    }
  };

  // Fonctions utilitaires pour l'analyse
  const extractConcepts = (content) => {
    const concepts = [];
    const lines = content.split('\n');
    
    lines.forEach(line => {
      if (line.includes('-') || line.includes('•')) {
        const concept = line.replace(/[-•\d.]/g, '').trim();
        if (concept && concept.length > 3) {
          concepts.push(concept);
        }
      }
    });
    
    return concepts.slice(0, 5);
  };

  const estimateDuration = (videos) => {
    const totalMinutes = videos.reduce((total, video) => {
      // Si le backend fournit durationInMinutes, l'utiliser directement
      if (video.durationInMinutes) {
        return total + video.durationInMinutes;
      }
      
      // Sinon, estimation simple basée sur le type de contenu
      // Vidéos éducatives typiques : 10-20 minutes
      return total + 15; // Estimation par défaut
    }, 0);

    return totalMinutes;
  };

  const estimateDifficulty = (videos) => {
    const scores = videos.map(v => v.semanticScore || 5);
    const avgScore = scores.reduce((a, b) => a + b, 0) / scores.length;
    
    if (avgScore >= 8) return 'Avancé';
    if (avgScore >= 6) return 'Intermédiaire';
    return 'Débutant';
  };

  return (
    <div className="w-full bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl shadow-lg border border-gray-200 p-6">
      {/* En-tête de la synthèse */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-start gap-4 flex-1">
          <div className="p-3 bg-purple-100 rounded-lg">
            <Sparkles className="h-6 w-6 text-purple-600" />
          </div>
          
          <div className="flex-1 min-w-0">
            <h3 className="text-xl font-semibold text-purple-900 mb-2">
              Synthèse de Découverte
            </h3>
            
            {/* Métadonnées */}
            <div className="flex items-center gap-4 flex-wrap mb-3">
              <div className="flex items-center gap-1 text-sm text-blue-600">
                <BookOpen className="h-4 w-4" />
                <span>{videos.length} vidéo{videos.length > 1 ? 's' : ''}</span>
              </div>
              
              <div className="flex items-center gap-1 text-sm text-green-600">
                <Clock className="h-4 w-4" />
                <span>{Math.ceil(estimateDuration(videos) / 60)}h</span>
              </div>
              
              <div className="flex items-center gap-1 text-sm text-purple-600">
                <Target className="h-4 w-4" />
                <span>{estimateDifficulty(videos)}</span>
              </div>
            </div>
            
            {/* Indicateurs universels */}
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-flex items-center gap-1 text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full">
                🎯 Analyse de Contenu
              </span>
              <span className="inline-flex items-center gap-1 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                🔄 Parcours de Découverte
              </span>
            </div>
          </div>
        </div>
        
        {/* Bouton de régénération */}
        <button
          onClick={generateSynthesis}
          disabled={isGenerating}
          className="flex items-center gap-2 px-3 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
          title="Régénérer la synthèse"
        >
          <RefreshCw className={`h-4 w-4 ${isGenerating ? 'animate-spin' : ''}`} />
          {isGenerating ? 'Génération...' : 'Actualiser'}
        </button>
      </div>

      {/* Contenu de la synthèse */}
      <div className="bg-white/70 border border-purple-200 rounded-lg p-6">
        {isGenerating ? (
          <div className="flex items-center justify-center py-8 text-purple-600">
            <RefreshCw className="h-6 w-6 animate-spin mr-3" />
            <span className="text-lg">Génération de la synthèse en cours...</span>
          </div>
        ) : (
          <>
            {error && (
              <div className="bg-red-50 border border-red-200 rounded p-4 text-red-700 mb-4">
                {error}
                <button 
                  onClick={generateSynthesis}
                  className="ml-2 px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors"
                >
                  Réessayer
                </button>
              </div>
            )}

            {synthesis && !isGenerating && (
              <div className="space-y-6">
                {/* Métriques rapides */}
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <Clock className="h-5 w-5 mx-auto mb-2 text-blue-600" />
                    <div className="font-semibold text-blue-800">
                      {Math.ceil(synthesis.estimatedDuration / 60)}h
                    </div>
                    <div className="text-xs text-blue-600">Durée totale</div>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-lg border border-green-200">
                    <Target className="h-5 w-5 mx-auto mb-2 text-green-600" />
                    <div className="font-semibold text-green-800">
                      {synthesis.difficultyLevel}
                    </div>
                    <div className="text-xs text-green-600">Niveau</div>
                  </div>
                  <div className="text-center p-3 bg-purple-50 rounded-lg border border-purple-200">
                    <BookOpen className="h-5 w-5 mx-auto mb-2 text-purple-600" />
                    <div className="font-semibold text-purple-800">
                      {synthesis.videoCount}
                    </div>
                    <div className="text-xs text-purple-600">Vidéos</div>
                  </div>
                </div>

                {/* Contenu de la synthèse avec EnhancedMarkdown et gestion des liens vidéos */}
                <div className="border-t border-purple-200 pt-4">
                  <EnhancedMarkdown 
                    content={synthesis.content}
                    synthesisArticles={videos.map(video => ({
                      ...video,
                      title: video.title,
                      url: video.url,
                      source: video.theme || 'Academy'
                    }))}
                    onReadNews={(video) => {
                      if (video.url && video.url !== '#') {
                        window.open(video.url, '_blank', 'noopener,noreferrer');
                      }
                    }}
                    className="text-gray-800"
                  />
                </div>

                {/* Concepts clés */}
                {synthesis.concepts && synthesis.concepts.length > 0 && (
                  <div className="border-t border-purple-200 pt-4">
                    <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Target className="h-5 w-5 text-purple-600" />
                      Concepts clés identifiés
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {synthesis.concepts.map((concept, index) => (
                        <span 
                          key={index}
                          className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full border border-blue-200"
                        >
                          {concept}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {!synthesis && !isGenerating && !error && (
              <div className="text-center py-8">
                <button 
                  onClick={generateSynthesis}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors mx-auto"
                >
                  <Sparkles className="h-4 w-4" />
                  Générer la synthèse de découverte
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Sources utilisées (vidéos) */}
      {videos && videos.length > 0 && (
        <div className="mt-6 pt-4 border-t border-purple-200">
          <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-purple-600" />
            Vidéos utilisées pour cette synthèse
          </h4>
          
          <div className="grid gap-3">
            {videos.slice(0, 4).map((video, index) => (
              <div
                key={`video-${video.id || index}`}
                className="group bg-white/70 border border-purple-200 rounded-lg p-4 hover:bg-purple-50 hover:border-purple-300 transition-all duration-200"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <h5 className="font-medium text-gray-900 group-hover:text-purple-700 text-sm leading-tight mb-1">
                      {video.title || 'Vidéo sans titre'}
                    </h5>
                    
                    <p className="text-xs text-gray-600 group-hover:text-purple-600 mb-2">
                      {video.theme || 'Thème général'}
                      {video.duration && (
                        <span className="ml-2">• {video.duration}</span>
                      )}
                    </p>
                    
                    {video.description && (
                      <p className="text-xs text-gray-500 line-clamp-2">
                        {video.description}
                      </p>
                    )}
                    
                    {/* Métadonnées vidéo */}
                    <div className="flex items-center gap-2 mt-2">
                      {video.semanticScore && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                          Score : {video.semanticScore.toFixed(1)}/10
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            
            {/* Indicateur s'il y a plus de vidéos */}
            {videos.length > 4 && (
              <div className="text-center text-sm text-gray-500 py-2 bg-white/50 rounded-lg border border-purple-100">
                + {videos.length - 4} autre{videos.length - 4 > 1 ? 's' : ''} vidéo{videos.length - 4 > 1 ? 's' : ''} utilisée{videos.length - 4 > 1 ? 's' : ''} dans cette synthèse
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AcademySynthesisCard;
